import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Save, Plane, RefreshCw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminVacation() {
  const { user, loading } = useAuth();
  const [form, setForm] = useState({
    active: false,
    title: "Wir sind auf Betriebsurlaub",
    message: "Am 10.08.2026 geht es weiter. Wir freuen uns auf Sie!",
    return_date: "10.08.2026",
  });
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/settings").then((r) => {
      if (r.data?.vacation) setForm(r.data.vacation);
    }).catch(() => {}).finally(() => setLoadingData(false));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/vacation", form);
      toast.success(form.active ? "Betriebsurlaub aktiviert — Overlay ist live" : "Betriebsurlaub deaktiviert");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-128px)]" data-testid="vacation-admin-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-2">
            <ArrowLeft size={14} /> Zurück zum Admin
          </Link>
          <div className="flex items-center gap-3">
            <Plane size={24} strokeWidth={1.4} className="text-amber-500" />
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Betriebsurlaub</h1>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Blendet auf der Startseite und allen öffentlichen Seiten ein Vollbild-Overlay ein. Der Admin-Bereich bleibt weiterhin nutzbar.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {loadingData ? (
          <p className="text-sm text-slate-500">Lade…</p>
        ) : (
          <>
            {/* Toggle */}
            <div className="bg-white border border-slate-200 p-6">
              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set("active", e.target.checked)}
                  data-testid="vacation-active"
                  className="mt-1 w-5 h-5 accent-amber-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {form.active ? <Eye size={16} className="text-emerald-600" /> : <EyeOff size={16} className="text-slate-400" />}
                    <span className="font-medium text-slate-900">Betriebsurlaub-Overlay {form.active ? "aktiv" : "inaktiv"}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {form.active
                      ? "Kunden sehen aktuell das Overlay — sie können nicht auf Inserate klicken."
                      : "Website funktioniert normal — kein Overlay sichtbar."}
                  </p>
                </div>
              </label>
            </div>

            {/* Content */}
            <div className="bg-white border border-slate-200 p-6 space-y-5">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-widest text-slate-900">Inhalt des Overlays</h2>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">Überschrift</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  data-testid="vacation-title"
                  className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">Nachricht</label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="vacation-message"
                  className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900 resize-none"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">Rückkehr-Datum</label>
                <input
                  type="text"
                  value={form.return_date}
                  onChange={(e) => set("return_date", e.target.value)}
                  placeholder="10.08.2026"
                  data-testid="vacation-return"
                  className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900"
                />
              </div>
            </div>

            {/* Live-Vorschau */}
            <div className="bg-slate-900 text-white p-8 md:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 grain pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3">Vorschau</div>
                <img src="/logo.png" alt="Logo" className="h-16 mx-auto mb-4 object-contain" />
                <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">{form.title || "—"}</h3>
                {form.message && <p className="font-serif italic text-base text-slate-300 mb-4 max-w-md mx-auto">{form.message}</p>}
                {form.return_date && (
                  <div className="inline-block border-y border-white/20 py-3 px-5">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-slate-400">Wieder für Sie da ab</div>
                    <div className="font-heading font-bold text-xl">{form.return_date}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Save */}
            <button
              onClick={save}
              disabled={saving}
              data-testid="vacation-save"
              className="w-full bg-slate-900 text-white py-4 text-sm font-medium hover:bg-amber-500 hover:text-slate-900 transition-crisp disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <><RefreshCw size={14} className="animate-spin" /> Speichere…</> : <><Save size={14} /> Speichern</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
